import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message, Card, DatePicker,
  Space, Tag, Popconfirm, Spin, Row, Col, Drawer, List, Typography, Radio, Tooltip, Popover
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  UserOutlined, FileTextOutlined, TeamOutlined, ReloadOutlined, MobileOutlined,
  CopyOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import dayjs from 'dayjs';
import DepartmentSelect from '../components/DepartmentSelect';
import PositionSelect from '../components/PositionSelect';
import AttachmentUpload from '../components/AttachmentUpload';
import ImageViewer from '../components/ImageViewer';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const Interviews = () => {
  const location = useLocation();
  const { user, hasPermission } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [leads, setLeads] = useState([]);
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [suitableModalVisible, setSuitableModalVisible] = useState(false);
  const [onboardingModalVisible, setOnboardingModalVisible] = useState(false);
  const [currentInterview, setCurrentInterview] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [isReschedule, setIsReschedule] = useState(false);
  const [followUpDrawerVisible, setFollowUpDrawerVisible] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [candidateLead, setCandidateLead] = useState(null);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [candidateAttachments, setCandidateAttachments] = useState([]);
  const [interviewFilesMap, setInterviewFilesMap] = useState({}); // interviewId -> files
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    interviewer: '',
    position_id: '',
    date_range: null,
    interview_id: '',
    created_by: ''
  });
  const [openStatusPopoverKey, setOpenStatusPopoverKey] = useState(null); // 常驻提醒：当前打开的状态Popover
  const [eliminateUploadFiles, setEliminateUploadFiles] = useState([]);
  // 服务端分页
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  // 文本筛选防抖后的值（search/interviewer/interview_id 每个按键不立即发请求）
  const [debouncedText, setDebouncedText] = useState({ search: '', interviewer: '', interview_id: '' });
  // 线索数据仅在打开发起/重排弹窗时按需加载
  const leadsLoadedRef = useRef(false);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [suitableForm] = Form.useForm();
  const [onboardingForm] = Form.useForm();

  // 打开图片预览
  const openImageViewer = (imageUrl, imageName, downloadUrl) => {
    if (imageUrl) {
      setCurrentImage({ 
        url: imageUrl, 
        name: imageName || '图片', 
        downloadUrl: downloadUrl || imageUrl 
      });
      setImageViewerVisible(true);
    }
  };

  // 关闭图片预览
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [onboardingDepartmentId, setOnboardingDepartmentId] = useState(null);
  const eliminateUploadRef = useRef(null);
  const suitableUploadRef = useRef(null);
  const [suitableUploadFiles, setSuitableUploadFiles] = useState([]);
  const [highlightedInterviewId, setHighlightedInterviewId] = useState(null);

  // 检查是否有从线索详情页面传递过来的搜索参数
  useEffect(() => {
    if (location.state) {
      const { searchInterviewId, highlightInterview } = location.state;
      if (searchInterviewId && highlightInterview) {
        console.log('🔍 接收到面试记录搜索参数:', searchInterviewId);
        setHighlightedInterviewId(searchInterviewId);
        
        // 自动设置面试ID筛选
        setFilters(prev => ({
          ...prev,
          interview_id: searchInterviewId.toString()
        }));
        
        // 清空location.state，避免重复处理
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state]);

  // 文本类筛选防抖：停止输入 400ms 后才更新，避免每个按键都打接口
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText({
        search: filters.search,
        interviewer: filters.interviewer,
        interview_id: filters.interview_id
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search, filters.interviewer, filters.interview_id]);

  // 初始化岗位/用户（只需一次）
  useEffect(() => {
    fetchPositions();
    fetchUsers();
  }, []);

  // 唯一的数据加载入口：筛选条件（文本已防抖）或分页变化时重新获取
  const dateStartKey = filters.date_range?.[0]?.format('YYYY-MM-DD') || '';
  const dateEndKey = filters.date_range?.[1]?.format('YYYY-MM-DD') || '';
  useEffect(() => {
    fetchInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedText.search, debouncedText.interviewer, debouncedText.interview_id,
    filters.status, filters.position_id, filters.created_by,
    dateStartKey, dateEndKey,
    pagination.current, pagination.pageSize
  ]);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // 文本字段使用防抖后的值
      const activeFilters = {
        ...filters,
        search: debouncedText.search,
        interviewer: debouncedText.interviewer,
        interview_id: debouncedText.interview_id
      };

      Object.keys(activeFilters).forEach(key => {
        if (activeFilters[key] !== '' && activeFilters[key] !== null && activeFilters[key] !== undefined) {
          if (key === 'date_range' && activeFilters[key]) {
            // 处理日期范围参数
            const [startDate, endDate] = activeFilters[key];
            if (startDate) {
              params.append('start_date', startDate.format('YYYY-MM-DD'));
            }
            if (endDate) {
              params.append('end_date', endDate.format('YYYY-MM-DD'));
            }
          } else if (key === 'interview_id' && activeFilters[key]) {
            // 处理面试记录ID参数
            params.append('interview_id', activeFilters[key]);
          } else {
            params.append(key, activeFilters[key]);
          }
        }
      });

      // 服务端分页参数
      params.append('page', pagination.current);
      params.append('limit', pagination.pageSize);

      const queryString = params.toString();
      const response = await api.get(`/interviews?${queryString}`);

      // 处理分页响应格式
      if (response.data && Array.isArray(response.data.data)) {
        setInterviews(response.data.data);
        const pg = response.data.pagination;
        if (pg) {
          setPagination(prev => ({
            ...prev,
            current: pg.page,
            pageSize: pg.limit,
            total: pg.total
          }));
        }
      } else if (Array.isArray(response.data)) {
        // 兼容旧数组格式
        setInterviews(response.data);
      }
    } catch (error) {
      console.error('获取面试列表失败:', error);
      message.error(error.response?.data?.message || '获取面试列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 线索列表只在打开发起面试/重新安排弹窗时加载一次（候选人下拉用）
  const ensureLeadsLoaded = async () => {
    if (leadsLoadedRef.current) return;
    try {
      const response = await api.get('/leads');
      if (response.data && response.data.data) {
        setLeads(response.data.data);
      } else {
        setLeads(Array.isArray(response.data) ? response.data : []);
      }
      leadsLoadedRef.current = true;
    } catch (error) {
      console.error('获取线索列表失败:', error);
      message.error(error.response?.data?.message || '获取线索列表失败');
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/positions');
      // 处理新的分页响应格式
      if (response.data && response.data.data) {
        setPositions(response.data.data);
      } else {
        setPositions(response.data);
      }
    } catch (error) {
      console.error('获取岗位列表失败:', error);
      message.error(error.response?.data?.message || '获取岗位列表失败');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      // 处理新的分页响应格式
      if (response.data && response.data.data) {
        setUsers(response.data.data);
      } else {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error(error.response?.data?.message || '获取用户列表失败');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // 任何筛选条件变化都回到第一页
    setPagination(prev => prev.current === 1 ? prev : { ...prev, current: 1 });
  };

  const handleClearAllFilters = () => {
    setFilters({
      search: '',
      status: '',
      interviewer: '',
      position_id: '',
      date_range: null,
      interview_id: '',
      created_by: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    form.resetFields();
    setIsReschedule(false);
    ensureLeadsLoaded();
    setModalVisible(true);
  };

  const handleStatusAction = (record, action) => {
    setCurrentInterview(record);
    setStatusAction(action);
    setEliminateUploadFiles([]);
    
    if (action === 'suitable') {
      // 合适：先上传面试工单，不直接打开入职
      suitableForm.resetFields();
      setSuitableUploadFiles([]);
      setSuitableModalVisible(true);
    } else {
      // 其他状态弹出状态更新模态框
      statusForm.resetFields();
      setStatusModalVisible(true);
    }
  };

  const handleStatusSubmit = async (values) => {
    try {
      const data = { status: statusAction };

      if (statusAction === 'eliminated') {
        // 处理淘汰状态
        data.rejection_reason = values.rejection_reason;
        // 至少需要一份面试表
        if (!eliminateUploadFiles || eliminateUploadFiles.length === 0) {
          message.error('请上传面试表');
          return;
        }
        // 使用第一份文件的URL
        data.rejection_file_url = eliminateUploadFiles[0]?.url || eliminateUploadFiles[0]?.fileData?.url || null;
        data.no_show_reason = null;
        data.suitable_notes = null;
      } else if (statusAction === 'no_show') {
        // 处理爽约状态
        data.no_show_reason = values.no_show_reason;
        data.rejection_reason = null;
        data.rejection_file_url = null;
        data.suitable_notes = null;
      }

      console.log('发送的状态更新数据:', data);
      // 带上本次上传会话ID和当前已选文件URL，便于后端写入到跟进记录
      const uploadId = window.__LAST_UPLOAD_ID__;
      const attachment_urls = Array.isArray(eliminateUploadFiles) ? eliminateUploadFiles.map(f => f.url || f.fileData?.url).filter(Boolean) : [];
      await api.put(`/interviews/${currentInterview.id}`, { ...data, uploadId, attachment_urls });

      // 绑定本次二维码会话中的文件到该面试工单（如果有）
      try {
        if (uploadId) {
          await api.post('/upload/bind-interview', { uploadId, interviewId: currentInterview.id });
          delete window.__LAST_UPLOAD_ID__;
        }
      } catch (bindErr) {
        console.warn('绑定面试工单附件失败:', bindErr?.message || bindErr);
      }

      message.success('状态更新成功');
      setStatusModalVisible(false);
      fetchInterviews();
    } catch (error) {
      console.error('状态更新失败:', error);
      message.error(error.response?.data?.message || '状态更新失败');
    }
  };

  const handleSuitableSubmit = async (values) => {
    try {
      // 至少需要一份面试工单
      if (!suitableUploadFiles || suitableUploadFiles.length === 0) {
        message.error('请上传面试工单');
        return;
      }
      const data = {
        status: 'suitable',
        suitable_notes: values?.suitable_notes || ''
      };
      const uploadId = window.__LAST_UPLOAD_ID__;
      const attachment_urls = suitableUploadFiles.map(f => f.url || f.fileData?.url).filter(Boolean);
      await api.put(`/interviews/${currentInterview.id}`, { ...data, uploadId, attachment_urls });

      // 绑定扫码会话中的文件到面试工单
      try {
        if (uploadId) {
          await api.post('/upload/bind-interview', { uploadId, interviewId: currentInterview.id });
          delete window.__LAST_UPLOAD_ID__;
        }
      } catch (e) {
        console.warn('绑定面试工单附件失败:', e?.message || e);
      }

      message.success('已标记为合适，并生成跟进记录');
      setSuitableModalVisible(false);
      fetchInterviews();
    } catch (error) {
      console.error('标记合适失败:', error);
      message.error(error.response?.data?.message || '标记合适失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 获取选中的岗位和部门信息
      const positionResponse = await api.get(`/positions/${values.position_id}`);
      const position = positionResponse.data;
      
      const departmentResponse = await api.get(`/departments/${values.department_id}`);
      const department = departmentResponse.data;
      
      const data = {
        lead_id: values.lead_id,
        position: position.name, // 保存岗位名称
        position_id: values.position_id, // 保存岗位ID
        department_id: values.department_id, // 保存部门ID
        interviewer: values.interviewer,
        interview_date: values.interview_date.format('YYYY-MM-DD HH:mm:ss'),
        notes: values.notes || ''
      };

      if (isReschedule && currentInterview) {
        // 重新安排：更新现有面试（不传递status字段）
        await api.put(`/interviews/${currentInterview.id}`, data);
        message.success('重新安排成功');
      } else {
        // 创建新面试时添加status字段
        data.status = 'pending';
        await api.post('/interviews', data);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      setIsReschedule(false);
      setCurrentInterview(null);
      fetchInterviews();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleOnboardingSubmit = async (values) => {
    try {
      console.log('提交的入职数据:', values);
      
      // 获取选中的岗位和部门信息
      const positionResponse = await api.get(`/positions/${values.position_id}`);
      const position = positionResponse.data;
      
      const departmentResponse = await api.get(`/departments/${values.department_id}`);
      const department = departmentResponse.data;
      
      const data = {
        employee_name: values.employee_name,
        position: position.name, // 保存岗位名称
        position_id: values.position_id, // 保存岗位ID
        department: department.name, // 保存部门名称
        department_id: values.department_id, // 保存部门ID
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        interview_id: currentInterview.id,
        notes: values.notes || ''
      };

      console.log('发送到后端的数据:', data);

      const response = await api.post('/onboarding', data);
      console.log('后端响应:', response);
      
      message.success('入职工单创建成功');
      setOnboardingModalVisible(false);
      onboardingForm.resetFields();
      
      // 同时更新面试状态为合适
      await api.put(`/interviews/${currentInterview.id}`, {
        status: 'suitable',
        suitable_notes: values.notes || '',
        no_show_reason: null,
        rejection_reason: null,
        rejection_file_url: null
      });
      
      fetchInterviews();
    } catch (error) {
      console.error('创建入职工单失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error(error.response?.data?.message || error.response?.data?.error || '创建入职工单失败');
    }
  };

  const handleReschedule = (record) => {
    console.log('重新安排面试记录:', record);

    // 懒加载候选人列表
    ensureLeadsLoaded();

    // 复制面试信息，但清空面试时间
    form.setFieldsValue({
      lead_id: record.lead_id,
      department_id: record.department_id,
      position_id: record.position_id,
      interviewer: record.interviewer,
      interview_date: null, // 清空面试时间
      notes: record.notes || ''
    });
    
    // 设置部门ID，这会触发岗位列表的重新加载
    setSelectedDepartmentId(record.department_id);
    setCurrentInterview(record); // 设置当前面试记录
    setIsReschedule(true);
    setModalVisible(true);
    
    console.log('表单字段设置完成:', {
      lead_id: record.lead_id,
      department_id: record.department_id,
      position_id: record.position_id,
      interviewer: record.interviewer
    });
  };

  const handleViewFollowUp = async (record) => {
    try {
      setCurrentCandidate(record);
      setFollowUpDrawerVisible(true);
      setCandidateLead(null);
      setCandidateAttachments([]);
      
      // 获取跟进记录、线索详情、附件
      const [followUpsRes, leadRes, attRes] = await Promise.all([
        api.get(`/leads/${record.lead_id}/follow-ups`),
        api.get(`/leads/${record.lead_id}`),
        api.get(`/upload/lead/${record.lead_id}`)
      ]);

      const followUps = Array.isArray(followUpsRes.data) ? followUpsRes.data : [];

      // 处理跟进历史
      const allHistory = followUps.map(followUp => {
        // 根据内容判断类型并设置图标
        let icon = <UserOutlined style={{ color: '#1890ff' }} />;
        let title = '线索跟进';
        
        if (followUp.content.includes('创建面试工单')) {
          icon = <CalendarOutlined style={{ color: '#52c41a' }} />;
          title = '面试工单';
        } else if (followUp.content.includes('面试状态更新')) {
          icon = <CalendarOutlined style={{ color: '#52c41a' }} />;
          title = '面试状态更新';
        } else if (followUp.content.includes('创建入职工单')) {
          icon = <FileTextOutlined style={{ color: '#722ed1' }} />;
          title = '入职管理';
        } else if (followUp.content.includes('员工正式入职')) {
          icon = <TeamOutlined style={{ color: '#13c2c2' }} />;
          title = '员工入职';
        }

        return {
          id: `followup_${followUp.id}`,
          type: 'followup',
          title: title,
          content: followUp.content,
          created_at: followUp.created_at,
          created_by_name: followUp.created_by_name,
          icon: icon
        };
      });

      // 按时间排序
      allHistory.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setFollowUpHistory(allHistory);
      setCandidateLead(leadRes.data || null);
      setCandidateAttachments(attRes.data?.data || []);
    } catch (error) {
      console.error('获取跟进历史/线索详情失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error(`获取跟进历史失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 复制面试战报
  const copyInterviewReport = (record) => {
    const report = `有新的面试了！\n` +
      `==========================\n` +
      `求职姓名: ${record.lead_name || '未知'}\n` +
      `约面电话: ${record.lead_phone || '未知'}\n` +
      `约面岗位: ${record.position_name || record.position || '未知'}\n` +
      `约面部门: ${record.department_name || '未知'}\n` +
      `约面时间: ${record.interview_date ? new Date(record.interview_date).toLocaleString('zh-CN') : '未知'}\n` +
      `面试官: ${record.interviewer || '未知'}\n` +
      `面试状态: ${getStatusText(record.status)}\n` +
      `备注: ${record.notes || '无'}\n` +
      `==========================\n` +
      `面试ID: ${record.id}\n` +
      `线索ID: ${record.lead_id}\n` +
      `创建人: ${record.created_by_name || record.created_by_username || '未知'}`;

    // 复制到剪贴板
    navigator.clipboard.writeText(report)
      .then(() => {
        message.success('面试战报已复制到剪贴板');
      })
      .catch(err => {
        console.error('复制失败:', err);
        message.error('复制失败，请手动复制');
      });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
      case 'scheduled': return 'blue';
      case 'eliminated': return 'red';
      case 'no_show': return 'orange';
      case 'suitable': return 'green';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
      case 'scheduled': return '待面试';
      case 'eliminated': return '淘汰';
      case 'no_show': return '爽约';
      case 'suitable': return '合适';
      default: return status;
    }
  };

  const ensureInterviewFiles = async (interviewId) => {
    if (!interviewId) return [];
    if (interviewFilesMap[interviewId]) return interviewFilesMap[interviewId];
    try {
      const res = await api.get(`/upload/interview/${interviewId}`);
      const files = res.data?.data || [];
      setInterviewFilesMap(prev => ({ ...prev, [interviewId]: files }));
      return files;
    } catch (e) {
      console.warn('获取面试工单附件失败:', e?.message || e);
      setInterviewFilesMap(prev => ({ ...prev, [interviewId]: [] }));
      return [];
    }
  };

  const columns = [
    {
      title: '候选人',
      dataIndex: 'lead_name',
      key: 'lead_name',
      width: 220,
      minWidth: 220,
      render: (text, record) => (
        <div 
          style={{ cursor: 'pointer' }}
          onClick={() => handleViewFollowUp(record)}
          title="点击查看跟进历史"
        >
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {text || `候选人ID: ${record.lead_id}`}
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
            线索ID: {record.lead_id || '-'} | 面试ID: {record.id}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.lead_phone || '-'}</div>
          {record.lead_source_name && (
            <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '2px' }}>
              📍 来源: {record.lead_source_name}
            </div>
          )}
        </div>
      )
    },
    {
      title: '面试职位',
      dataIndex: 'position_name',
      key: 'position_name',
      width: 150,
      render: (text, record) => {
        // 优先显示关联查询的名称，如果没有则显示position字段
        const positionName = record.position_name || record.position;
        const departmentName = record.department_name;
        
        if (positionName) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{positionName}</div>
              {departmentName && (
                <div style={{ fontSize: '12px', color: '#666' }}>{departmentName}</div>
              )}
              {/* 复制战报按钮 */}
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyInterviewReport(record)}
                size="small"
                style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#1890ff', marginTop: '4px', fontSize: '12px' }}
                title="复制面试战报"
              >
                复制战报
              </Button>
            </div>
          );
        }
        
        // 如果都没有，显示ID
        if (record.position_id) {
          return (
            <div>
              <div style={{ fontWeight: 'bold', color: '#999' }}>岗位ID: {record.position_id}</div>
              {record.department_id && (
                <div style={{ fontSize: '12px', color: '#999' }}>部门ID: {record.department_id}</div>
              )}
              {/* 复制战报按钮 */}
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyInterviewReport(record)}
                size="small"
                style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#1890ff', marginTop: '4px', fontSize: '12px' }}
                title="复制面试战报"
              >
                复制战报
              </Button>
            </div>
          );
        }
        
        return '-';
      }
    },
    {
      title: '面试官',
      dataIndex: 'interviewer',
      key: 'interviewer',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '面试时间',
      dataIndex: 'interview_date',
      key: 'interview_date',
      width: 160,
      render: (text) => new Date(text).toLocaleString('zh-CN')
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 100,
      render: (text, record) => {
        // 优先显示created_by_name，如果为空则显示created_by_username
        return record.created_by_name || record.created_by_username || '-';
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        // 爽约：使用 Tooltip 展示原因
        if (status === 'no_show') {
          const tip = record.no_show_reason ? `爽约原因：${record.no_show_reason}` : null;
          return (
            <Tooltip title={tip} placement="top">
              <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
            </Tooltip>
          );
        }

        // 淘汰：使用 Popover 展示淘汰原因与图片
        if (status === 'eliminated') {
          const content = (
            <div style={{ maxWidth: 360 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>淘汰理由：</Text>
                <span>{record.rejection_reason || '-'}</span>
              </div>
              <div>
                <Text strong>面试表：</Text>
                <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                  {/* 首选表字段中的单张 */}
                  {record.rejection_file_url ? (
                    <img
                      src={record.rejection_file_url}
                      alt="面试表"
                      style={{ width: 86, height: 86, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', cursor: 'zoom-in' }}
                      onClick={() => Modal.info({ title: '预览', content: (<div style={{textAlign:'center'}}><img src={record.rejection_file_url} alt="预览" style={{maxWidth:'100%', maxHeight:'70vh'}}/></div>), width: 800 })}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : null}
                  {/* 其余附件（若有绑定） */}
                  {(interviewFilesMap[record.id] || []).map(f => (
                    <img
                      key={f.id}
                      src={f.url}
                      alt={f.originalName}
                      style={{ width: 86, height: 86, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', cursor: 'zoom-in' }}
                      onClick={() => Modal.info({ title: f.originalName, content: (<div style={{textAlign:'center'}}><img src={f.url} alt="预览" style={{maxWidth:'100%', maxHeight:'70vh'}}/></div>), width: 800 })}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ))}
                  {!record.rejection_file_url && (!interviewFilesMap[record.id] || interviewFilesMap[record.id].length === 0) && (
                    <Text type="secondary">无面试表</Text>
                  )}
                </div>
              </div>
            </div>
          );

          return (
            <Popover
              content={content}
              title={null}
              placement="topLeft"
              trigger="click"
              open={openStatusPopoverKey === `elim_${record.id}`}
              onOpenChange={async (vis) => {
                if (vis) {
                  setOpenStatusPopoverKey(`elim_${record.id}`);
                  await ensureInterviewFiles(record.id);
                } else {
                  setOpenStatusPopoverKey(null);
                }
              }}
            >
              <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
            </Popover>
          );
        }

        // 合适：展示备注与上传的面试表
        if (status === 'suitable') {
          const content = (
            <div style={{ maxWidth: 360 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>备注：</Text>
                <span>{record.suitable_notes || '-'}</span>
              </div>
              <div>
                <Text strong>面试表：</Text>
                <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                  {(interviewFilesMap[record.id] || []).map(f => (
                    <img
                      key={f.id}
                      src={f.url}
                      alt={f.originalName}
                      style={{ width: 86, height: 86, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', cursor: 'zoom-in' }}
                      onClick={() => Modal.info({ title: f.originalName, content: (<div style={{textAlign:'center'}}><img src={f.url} alt="预览" style={{maxWidth:'100%', maxHeight:'70vh'}}/></div>), width: 800 })}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ))}
                  {(!interviewFilesMap[record.id] || interviewFilesMap[record.id].length === 0) && (
                    <Text type="secondary">无面试表</Text>
                  )}
                </div>
              </div>
            </div>
          );

          return (
            <Popover
              content={content}
              title={null}
              placement="topLeft"
              trigger="click"
              open={openStatusPopoverKey === `suit_${record.id}`}
              onOpenChange={async (vis) => {
                if (vis) {
                  setOpenStatusPopoverKey(`suit_${record.id}`);
                  await ensureInterviewFiles(record.id);
                } else {
                  setOpenStatusPopoverKey(null);
                }
              }}
            >
              <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
            </Popover>
          );
        }

        // 其他状态：默认展示
        return <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>;
      }
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      width: 300,
      maxWidth: 300,
      render: (text) => {
        if (!text) return '-';
        
        return (
          <Tooltip title={text} placement="top">
            <div 
              style={{ 
                maxWidth: '300px',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.4',
                maxHeight: '60px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
          {(record.status === 'pending' || record.status === 'scheduled') && (
            <>
              <Button
                type="link"
                onClick={() => handleStatusAction(record, 'suitable')}
                size="small"
                style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#52c41a' }}
              >
                合适
              </Button>
              <Button
                type="link"
                onClick={() => handleStatusAction(record, 'eliminated')}
                size="small"
                style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#ff4d4f' }}
              >
                淘汰
              </Button>
              <Button
                type="link"
                onClick={() => handleStatusAction(record, 'no_show')}
                size="small"
                style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#faad14' }}
                title={record.no_show_reason ? `爽约原因：${record.no_show_reason}` : "标记为爽约"}
              >
                爽约
              </Button>
            </>
          )}
          {record.status === 'suitable' && (
            <Button
              type="link"
              onClick={() => {
                setCurrentInterview(record);
                onboardingForm.setFieldsValue({
                  employee_name: record.lead_name,
                  position_id: record.position_id,
                  department_id: record.department_id,
                  start_date: null
                });
                setOnboardingDepartmentId(record.department_id);
                setOnboardingModalVisible(true);
              }}
              size="small"
              style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#52c41a' }}
            >
              创建入职
            </Button>
          )}
          {(record.status === 'eliminated' || record.status === 'no_show') && (
            <Button
              type="link"
              onClick={() => handleReschedule(record)}
              size="small"
              style={{ padding: '0', height: 'auto', lineHeight: '1.2', color: '#1890ff' }}
            >
              重新安排
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>
      {/* 筛选卡片 */}
      <Card 
        style={{ marginBottom: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>筛选条件</span>
            <Button 
              type="link" 
              onClick={handleClearAllFilters}
              style={{ padding: 0 }}
            >
              清除所有筛选
            </Button>
          </div>
        }
      >
        <Row gutter={[0, 15]}>
          <Col span={24}>
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              <Form.Item label="状态" style={{ marginBottom: 0, minWidth: '200px' }}>
                <Radio.Group
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="">全部</Radio.Button>
                  <Radio.Button value="pending">待面试</Radio.Button>
                  <Radio.Button value="suitable">合适</Radio.Button>
                  <Radio.Button value="eliminated">淘汰</Radio.Button>
                  <Radio.Button value="no_show">爽约</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              <Form.Item label="岗位" style={{ marginBottom: 0, minWidth: '400px' }}>
                <Select
                  placeholder="选择岗位"
                  value={filters.position_id}
                  onChange={(value) => handleFilterChange('position_id', value)}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {positions && positions.map(position => (
                    <Option key={position.id} value={position.id}>
                      {position.department_name ? `${position.department_name}-${position.name}` : position.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="搜索" style={{ marginBottom: 0, minWidth: '200px' }}>
                <Input
                  placeholder="候选人姓名/手机号"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="面试记录ID" style={{ marginBottom: 0, minWidth: '200px' }}>
                <Input
                  placeholder="输入面试记录ID"
                  value={filters.interview_id}
                  onChange={(e) => handleFilterChange('interview_id', e.target.value)}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
              {hasPermission('user.view') && (
                <Form.Item label="创建人" style={{ marginBottom: 0, minWidth: '200px' }}>
                  <Select
                    placeholder="选择创建人"
                    value={filters.created_by}
                    onChange={(value) => handleFilterChange('created_by', value)}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {users && users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.name || user.username}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>
          </Col>
          <Col span={6}>
            <Form.Item label="面试官" style={{ marginBottom: 0 }}>
              <Input
                placeholder="面试官姓名"
                value={filters.interviewer}
                onChange={(e) => handleFilterChange('interviewer', e.target.value)}
                allowClear
                style={{ width: '80%' }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="面试时间" style={{ marginBottom: 0 }}>
              <DatePicker.RangePicker
                value={filters.date_range}
                onChange={(dates) => handleFilterChange('date_range', dates)}
                placeholder={['开始日期', '结束日期']}
                style={{ width: '80%' }}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0 }}>面试管理</h1>
            <div style={{ 
              marginTop: '8px', 
              fontSize: '14px', 
              color: '#666',
              display: 'flex',
              gap: '16px',
              alignItems: 'center'
            }}>
              <span>📊 总面试记录: {pagination.total} 条</span>
              {pagination.total > 0 && (
                <span style={{ color: '#ff4d4f' }}>
                  ⚠️ 线索ID重复说明: 同一线索可能有多次面试记录
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchInterviews}
              loading={loading}
              title="刷新数据"
            >
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加面试
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={interviews}
            rowKey="id"
            rowClassName={(record) => {
              if (highlightedInterviewId && record.id === highlightedInterviewId) {
                return 'highlighted-row';
              }
              return '';
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              }
            }}
          />
        </Spin>
      </Card>

      <style jsx>{`
        .highlighted-row {
          background-color: #fff7e6 !important;
          border-left: 4px solid #fa8c16 !important;
        }
        .highlighted-row:hover {
          background-color: #fff2e8 !important;
        }
      `}</style>

      <Modal
        title={isReschedule ? "重新安排面试" : "创建面试"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="lead_id"
            label="候选人"
            rules={[{ required: true, message: '请选择候选人' }]}
          >
            <Select 
              placeholder="请选择候选人" 
              allowClear
              showSearch
              filterOption={(input, option) => {
                const lead = leads.find(l => l.id === option.value);
                if (!lead) return false;
                const searchText = input.toLowerCase();
                return (
                  lead.name.toLowerCase().includes(searchText) ||
                  lead.phone.includes(searchText)
                );
              }}
            >
              {leads.map(lead => (
                <Option key={lead.id} value={lead.id}>
                  {lead.name} - {lead.phone}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="department_id"
            label="部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <DepartmentSelect 
              placeholder="请选择部门" 
              onChange={(value) => {
                setSelectedDepartmentId(value);
                form.setFieldsValue({ position_id: undefined });
              }}
            />
          </Form.Item>

          <Form.Item
            name="position_id"
            label="面试职位"
            rules={[{ required: true, message: '请选择面试职位' }]}
          >
            <PositionSelect 
              placeholder="请选择面试职位" 
              departmentId={selectedDepartmentId}
              disabled={!selectedDepartmentId}
            />
          </Form.Item>

          <Form.Item
            name="interviewer"
            label="面试官"
          >
            <Input placeholder="请输入面试官姓名" />
          </Form.Item>

          <Form.Item
            name="interview_date"
            label="面试时间"
            rules={[{ required: true, message: '请选择面试时间' }]}
          >
            <DatePicker 
              showTime 
              placeholder="选择面试时间"
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              disabledDate={isReschedule ? undefined : (current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            initialValue="pending"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={4} placeholder="请输入备注信息" />
          </Form.Item>

          <Form.Item
            name="no_show_reason"
            label="爽约原因"
            hidden={form.getFieldValue('status') !== 'no_show'}
          >
            <TextArea rows={3} placeholder="请输入爽约原因" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 状态更新模态框 */}
      <Modal
        title={`${getStatusText(statusAction)} - ${currentInterview?.lead_name}`}
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusSubmit}
        >
          {statusAction === 'eliminated' && (
            <>
              <Form.Item
                name="rejection_reason"
                label="淘汰理由"
                rules={[{ required: true, message: '请输入淘汰理由' }]}
              >
                <TextArea rows={4} placeholder="请输入淘汰理由" />
              </Form.Item>

              <Form.Item label="上传面试表" required>
                <Row gutter={8}>
                  <Col span={19}>
                    <AttachmentUpload
                      ref={eliminateUploadRef}
                      compact
                      hideList={false}
                      showQrButton={false}
                      attachToLead={false}
                      interviewId={currentInterview?.id}
                      onChange={(list) => setEliminateUploadFiles(list)}
                    />
                  </Col>
                  <Col span={5} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    <Button
                      type="primary"
                      icon={<MobileOutlined />}
                      onClick={() => eliminateUploadRef.current?.openQRCode()}
                      style={{ width: '100%' }}
                    >
                      手机上传
                    </Button>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 6, textAlign: 'center' }}>手机直接上传文件</div>
                  </Col>
                </Row>
              </Form.Item>
            </>
          )}

          {statusAction === 'no_show' && (
            <Form.Item
              name="no_show_reason"
              label="爽约原因"
              rules={[{ required: true, message: '请输入爽约原因' }]}
            >
              <TextArea rows={4} placeholder="请输入爽约原因" />
            </Form.Item>
          )}



          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setStatusModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确认
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 合适 → 上传面试工单 模态框 */}
      <Modal
        title={`上传面试工单 - ${currentInterview?.lead_name || ''}`}
        open={suitableModalVisible}
        onCancel={() => setSuitableModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={suitableForm} layout="vertical" onFinish={handleSuitableSubmit}>
          <Form.Item label="上传面试工单" required>
            <AttachmentUpload
              ref={suitableUploadRef}
              attachToLead={false}
              interviewId={currentInterview?.id}
              onChange={(list) => setSuitableUploadFiles(list)}
            />
          </Form.Item>

          <Form.Item name="suitable_notes" label="备注（可选）">
            <TextArea rows={3} placeholder="可填写面试评价等" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setSuitableModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">确认</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 入职工单创建模态框 */}
      <Modal
        title={`创建入职工单 - ${currentInterview?.lead_name}`}
        open={onboardingModalVisible}
        onCancel={() => setOnboardingModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={onboardingForm}
          layout="vertical"
          onFinish={handleOnboardingSubmit}
        >
          <Form.Item
            name="employee_name"
            label="员工姓名"
            rules={[{ required: true, message: '请输入员工姓名' }]}
          >
            <Input placeholder="请输入员工姓名" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <DepartmentSelect 
              placeholder="请选择部门" 
              onChange={(value) => {
                setOnboardingDepartmentId(value);
                onboardingForm.setFieldsValue({ position_id: undefined });
              }}
            />
          </Form.Item>

          <Form.Item
            name="position_id"
            label="职位"
            rules={[{ required: true, message: '请选择职位' }]}
          >
            <PositionSelect 
              placeholder="请选择职位" 
              departmentId={onboardingDepartmentId}
              disabled={!onboardingDepartmentId}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="入职日期"
            rules={[{ required: true, message: '请选择入职日期' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择入职日期" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={4} placeholder="请输入备注信息（可选）" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setOnboardingModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建入职工单
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 跟进历史抽屉（含基础详情与附件） */}
      <Drawer
        title={`${currentCandidate?.lead_name || ''} - 跟进历史`}
        open={followUpDrawerVisible}
        onClose={() => setFollowUpDrawerVisible(false)}
        width={700}
        placement="right"
        destroyOnClose
      >
        {/* 基础详情 */}
        {candidateLead && (
          <Card size="small" title="基础信息" style={{ marginBottom: 12 }}>
            <Row gutter={[16, 6]}>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>姓名：</strong>{candidateLead.name}</div></Col>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>手机：</strong>{candidateLead.phone}</div></Col>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>性别：</strong>{candidateLead.gender === 'male' ? '男' : candidateLead.gender === 'female' ? '女' : '未知'}</div></Col>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>年龄：</strong>{candidateLead.age || '-'}</div></Col>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>学历：</strong>{candidateLead.education || candidateLead.education_level || '-'}</div></Col>
              <Col span={12}><div style={{ lineHeight: 1.4 }}><strong>来源：</strong>{candidateLead.source_name || candidateLead.source || '-'}</div></Col>
              <Col span={24}><div style={{ lineHeight: 1.4 }}><strong>备注：</strong>{candidateLead.notes || '-'}</div></Col>
            </Row>
            {/* 附件 */}
            <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 10, paddingTop: 10 }}>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>附件</Title>
              {candidateAttachments.length === 0 ? (
                <Text type="secondary">暂无附件</Text>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                  {candidateAttachments.map((f) => {
                    const isImg = (f.mimetype && f.mimetype.startsWith('image/')) || f.isImage || (f.url && /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.url));
                    return (
                      <div key={f.id} style={{ textAlign: 'center' }}>
                        {isImg ? (
                          <img 
                            src={f.url} 
                            alt={f.originalName}
                            style={{ width: 86, height: 86, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                            onClick={() => openImageViewer(f.url, f.originalName, f.url)}
                          />
                        ) : (
                          <div style={{ width: 86, height: 86, border: '1px solid #eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                            <FileTextOutlined style={{ fontSize: 22, color: '#999' }} />
                          </div>
                        )}
                        <div style={{ marginTop: 4, fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.originalName}>{f.originalName}</div>
                        <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看/下载</a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* 跟进历史 */}
        {followUpHistory.length > 0 ? (
          <List
            dataSource={followUpHistory}
            renderItem={(item) => (
              <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {item.icon}
                      <Text strong style={{ fontSize: '14px' }}>{item.title}</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {item.created_by_name} · {new Date(item.created_at).toLocaleString('zh-CN')}
                      </Text>
                    </div>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {(() => {
                      const text = item.content || '';
                      const urlRegex = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/g;
                      const tokens = text.split(urlRegex);
                      return (
                        <div>
                          {tokens.map((tok, idx) => {
                            if (!tok) return null;
                            if (urlRegex.test(tok)) {
                              return (
                                <span key={idx} style={{ display: 'inline-block', maxWidth: '100%' }}>
                                  <a href={tok} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{tok}</a>
                                  <img
                                    src={tok}
                                    alt="follow-up-img"
                                    style={{ width: '30%', maxWidth: 240, borderRadius: 6, marginTop: 6, display: 'block', cursor: 'zoom-in' }}
                                    onClick={() => openImageViewer(tok, '跟进图片', tok)}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                </span>
                              );
                            }
                            return <span key={idx}>{tok}</span>;
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>
            暂无跟进历史
          </div>
        )}
      </Drawer>

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
    </main>
  );
};

export default Interviews; 